// Pure slide-management ops for a carousel deck Composition. Per-slide LAYER editing reuses
// composition-ops (each slide IS a PostComposition); these operate at the DECK level: add,
// delete, duplicate, reorder. Never mutate the input; every op flips deck.meta.dirty so the
// autosave persists it and publish re-renders the dirty slides.

import type { CarouselComposition, PostComposition, SlideRole } from '../types'
import { clone } from './composition-ops'

// Instagram deck bounds (mirror INSTAGRAM_CAROUSEL_MIN/MAX_ITEMS on the server).
export const MIN_SLIDES = 2
export const MAX_SLIDES = 10

let SEQ = 0
export function newSlideId(): string {
  SEQ = (SEQ + 1) % 1_000_000
  return `sl_${Date.now().toString(36)}_${SEQ.toString(36)}`
}

function withSlides(deck: CarouselComposition, slides: PostComposition[]): CarouselComposition {
  return { ...deck, slides, meta: { ...deck.meta, dirty: true } }
}

/** A deep copy of a slide with a fresh stable id + the given role, marked dirty. */
function freshSlide(src: PostComposition, role: SlideRole): PostComposition {
  const copy = clone(src)
  copy.slide = { id: newSlideId(), role }
  copy.meta = { ...copy.meta, dirty: true }
  return copy
}

/** Duplicate slide `index` in place (inserted right after it), keeping its role. */
export function duplicateSlide(deck: CarouselComposition, index: number): CarouselComposition {
  const src = deck.slides[index]
  if (!src || deck.slides.length >= MAX_SLIDES) return deck
  const copy = freshSlide(src, src.slide?.role ?? 'body')
  const slides = [...deck.slides.slice(0, index + 1), copy, ...deck.slides.slice(index + 1)]
  return withSlides(deck, slides)
}

/** Append a new body slide (seeded from the last slide's look) to the end of the deck. */
export function addSlide(deck: CarouselComposition): CarouselComposition {
  if (deck.slides.length >= MAX_SLIDES) return deck
  const template = deck.slides[deck.slides.length - 1] ?? deck.slides[0]
  if (!template) return deck
  return withSlides(deck, [...deck.slides, freshSlide(template, 'body')])
}

/** Remove slide `index` (guarded to keep the deck at/above the platform minimum). */
export function deleteSlide(deck: CarouselComposition, index: number): CarouselComposition {
  if (deck.slides.length <= MIN_SLIDES) return deck
  if (index < 0 || index >= deck.slides.length) return deck
  return withSlides(
    deck,
    deck.slides.filter((_, i) => i !== index)
  )
}

/** Move a slide from one swipe position to another (drag-to-reorder). */
export function moveSlide(
  deck: CarouselComposition,
  from: number,
  to: number
): CarouselComposition {
  if (from === to || from < 0 || to < 0 || from >= deck.slides.length || to >= deck.slides.length)
    return deck
  const slides = [...deck.slides]
  const [moved] = slides.splice(from, 1)
  slides.splice(to, 0, moved!)
  return withSlides(deck, slides)
}
